import styled, { css } from "styled-components"

import { SocketConnectionState } from "./reducer/common.types"

/**
 * The different screen sizes we differentiate between
 */
type DeviceKinds =
  | "mobileS"
  | "mobileM"
  | "mobileL"
  | "tabletS"
  | "tablet"
  | "laptop"
  | "laptopL"
  | "desktop"

/**
 * Width (in pixels) of screens corresponding to our screen size limits
 */
export const deviceSizes: { [key in DeviceKinds]: number } = {
  mobileS: 320,
  mobileM: 375,
  mobileL: 425,
  tabletS: 600,
  tablet: 768,
  laptop: 1024,
  laptopL: 1440,
  desktop: 2560
}

const deviceSizesPx = {
  mobileS: `${deviceSizes.mobileS}px`,
  mobileM: `${deviceSizes.mobileM}px`,
  mobileL: `${deviceSizes.mobileL}px`,
  tabletS: `${deviceSizes.tabletS}px`,
  tablet: `${deviceSizes.tablet}px`,
  laptop: `${deviceSizes.laptop}px`,
  laptopL: `${deviceSizes.laptopL}px`,
  desktop: `${deviceSizes.desktop}px`
}

/**
 * For each device size, there's a string that can
 * be inserted into a media query to target that
 * device size
 */
export const device: {
  smallerThen: { [key in DeviceKinds]: string }
  biggerThen: { [key in DeviceKinds]: string }
} = {
  smallerThen: {
    mobileS: `(max-width: ${deviceSizesPx.mobileS})`,
    mobileM: `(max-width: ${deviceSizesPx.mobileM})`,
    mobileL: `(max-width: ${deviceSizesPx.mobileL})`,
    tabletS: `(max-width: ${deviceSizesPx.tabletS})`,
    tablet: `(max-width: ${deviceSizesPx.tablet})`,
    laptop: `(max-width: ${deviceSizesPx.laptop})`,
    laptopL: `(max-width: ${deviceSizesPx.laptopL})`,
    desktop: `(max-width: ${deviceSizesPx.desktop})`
  },
  biggerThen: {
    mobileS: `(min-width: ${deviceSizesPx.mobileS})`,
    mobileM: `(min-width: ${deviceSizesPx.mobileM})`,
    mobileL: `(min-width: ${deviceSizesPx.mobileL})`,
    tabletS: `(min-width: ${deviceSizesPx.tabletS})`,
    tablet: `(min-width: ${deviceSizesPx.tablet})`,
    laptop: `(min-width: ${deviceSizesPx.laptop})`,
    laptopL: `(min-width: ${deviceSizesPx.laptopL})`,
    desktop: `(min-width: ${deviceSizesPx.desktop})`
  }
}

export const monospaceCss = css`
  background-color: whitesmoke;
  font-family: monospace;
  color: black;
  padding: 1em;
`

export const Button = styled.button`
  background-color: white;
  color: black;
  border-radius: 7px;
  border: 2px solid #d7d7d7;

  margin: 0.5em 1em;

  padding: 0.25em 1em;
  min-width: 5em;
  max-height: 10vh;
`

const RED_HEX = "#b34f4f"
const GREEN_HEX = "#476d2f"

export const GreenButton = styled(Button)`
  background-color: ${GREEN_HEX};
  border-color: ${GREEN_HEX};
  color: white;
`

export const RegularText = styled.p`
  color: white;
`

/** Removes the usual top margin of `p` elements,
 * and adds some at the bottom
 */
export const BottomMarginText = styled(RegularText)`
  margin-bottom: 0;
  margin-top: 1em;
`

export const ItalicText = styled(RegularText)`
  font-style: italic;
  color: #d7d7d7;
`

export const HeaderText = styled.h3`
  color: white;
`
export const Monospace = styled(RegularText)`
  font-family: monospace;
  margin: 0.5em;
  color: #ffffff80;
`

export const SmallerMonospace = styled(Monospace)`
  font-size: 80%;
`

export const BoldMonospace = styled(Monospace)`
  font-size: 130%;
  font-weight: bold;
`

export const ConnectionButton = styled(Button)<{
  connection: SocketConnectionState
}>`
  margin: 0;
  margin-left: 1em;
  width: 10em;

  /* 
  at this point the "connect" button is most likely wrapping to the next line 
  we therefore add some horizontal margin and remove the right margin
  */
  @media ${device.smallerThen.tabletS} {
    margin-left: 0;
    margin-top: 1em;
  }

  ${({ connection }) => {
    switch (connection) {
      case "connected":
        return css`
          background-color: ${RED_HEX};
          border: none;
          color: white;
        `
      case "disconnected":
        return css`
          background-color: ${GREEN_HEX};
          border: none;
          color: white;
        `
      case "disconnecting":
      case "connecting":
      case "subscribed":
        return css`
          background-color: #d4cd47;
          border: none;
          color: white;
        `
    }
  }}
`

/**
 * The root container of the API explorer.
 *
 * Layout is controlled by CSS Grid.
 * Nice guide on this: https://css-tricks.com/snippets/css/complete-guide-grid/
 *
 * The layout is currently differentiated on three sizes: desktop, laptop and tablet/phone.
 * New layouts can be added quite easily by using the media query strings provided above
 * with `device`.
 */
export const RootContainer = styled.div`
  height: 100vh;
  width: 100vw;

  padding: 1.5em;

  display: grid;
  grid-gap: 1em;

  grid-template-columns: 2fr 3fr;
  grid-template-rows: 1fr 1.7fr 6fr;
  grid-template-areas:
    "header-logo header-connection"
    "invoice messages"
    "build-request messages";

  @media ${device.smallerThen.laptop} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 0.5fr 2fr 3fr;

    grid-template-areas:
      "header-logo header-logo"
      "header-connection header-connection"
      "invoice messages"
      "build-request messages";
  }

  @media ${device.smallerThen.tabletS} {
    height: auto;
    max-height: 300vh;

    grid-template-columns: 1fr;
    grid-template-rows: 0.75fr 0.75fr 4fr 2fr 4fr;

    grid-template-areas:
      "header-logo"
      "header-connection"
      "build-request"
      "invoice"
      "messages";
  }
`
